import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAPI } from '../../Contexts/APIContext'
import styles from './index.module.css'

const date = value => (value ? new Date(value).toLocaleString() : null)

// Mac source workers: approve a coworker's Mac by the code its Feels Ingest
// app shows, see who approved what and when, and revoke a Mac in one click.
// A revoked Mac loses AdminAPI access immediately and Temporal access within
// its 15-minute token.
export default function Workers() {
  const api = useAPI()
  const queryClient = useQueryClient()
  const [code, setCode] = useState('')
  const [candidate, setCandidate] = useState(null)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const query = useQuery({
    queryKey: ['ingest', 'workers'],
    queryFn: () => api.getIngestWorkers(),
    retry: false,
    meta: { suppressGlobalError: true },
    refetchInterval: q => (q.state.error ? false : 15000),
  })
  const accessDenied = [401, 403].includes(query.error?.status)
  async function action(fn) {
    setBusy(true)
    setMessage('')
    try {
      await fn()
      await queryClient.invalidateQueries({ queryKey: ['ingest', 'workers'] })
    } catch (error) {
      setMessage(error.message || 'Worker access could not be changed.')
    } finally {
      setBusy(false)
    }
  }
  const active = machine => !machine.revoked_at && Date.parse(machine.expires_at) > Date.now()
  const connected = machine => active(machine) && machine.last_seen && Date.now() - Date.parse(machine.last_seen) < 120000
  return (
    <main className={styles.wrapper}>
      <header>
        <h1>Ingest workers</h1>
        <p><Link to="/ingest">← All ingest requests</Link></p>
        <p>
          Approve a Mac using the code shown in its Feels Ingest app. Only approve a Mac you recognize.
          An approved Mac fetches YouTube source data for requests; it can never commit catalogue changes.
        </p>
      </header>
      {accessDenied && <p className={styles.warning}>Ingestion worker administration is restricted to internal administrators.</p>}
      {!accessDenied && query.error && (
        <p className={styles.warning}>
          {query.error.message || 'Worker status is unavailable.'}{' '}
          <button onClick={() => query.refetch()}>Try again</button>
        </p>
      )}
      {!accessDenied && (
        <section className={styles.card}>
          <h2>Approve a Mac</h2>
          <form onSubmit={event => { event.preventDefault(); action(async () => setCandidate(await api.lookupIngestWorker(code))) }}>
            <label>
              Mac sign-in code{' '}
              <input value={code} maxLength={10} autoComplete="off" placeholder="10-character code"
                onChange={event => { setCode(event.target.value.replace(/\s/g, '').toUpperCase()); setCandidate(null) }} />
            </label>{' '}
            <button disabled={busy || !/^[A-F0-9]{10}$/.test(code)}>Look up Mac</button>
          </form>
          {candidate && (
            <section aria-label="Approve Mac">
              <p>
                Approve <strong>{candidate.host}</strong> for <strong>{candidate.environment}</strong> using release {candidate.release}?
              </p>
              <p>
                This signs a 7-day certificate for the Mac and lets it receive source-fetching work until access
                expires or is revoked. It does not submit any ingestion request.
              </p>
              <div className={styles.actions}>
                <button disabled={busy} onClick={() => action(async () => {
                  await api.approveIngestWorker(code, candidate.id)
                  setCandidate(null)
                  setCode('')
                  setMessage('Mac approved. Choose Finish Sign-in in the Mac app, then Start.')
                })}>Approve this Mac</button>{' '}
                <button disabled={busy} onClick={() => setCandidate(null)}>Cancel</button>
              </div>
            </section>
          )}
          {message && <p role="status">{message}</p>}
        </section>
      )}
      {query.data && (
        <section className={styles.card}>
          <h2>Approved Macs · {query.data.environment}</h2>
          {query.data.machines.length === 0 && <p>No Macs have been approved yet.</p>}
          {query.data.machines.map(machine => (
            <p key={machine.id}>
              <strong>{machine.host}</strong> · release {machine.release} ·{' '}
              {machine.revoked_at ? `revoked ${date(machine.revoked_at)}`
                : !active(machine) ? `expired ${date(machine.expires_at)}`
                : connected(machine) ? 'connected' : 'not connected'}
              {' '}· approved by {machine.approved_by} {date(machine.approved_at)}
              {machine.certificate_expires && ` · certificate until ${date(machine.certificate_expires)}`}
              {machine.last_seen && ` · last seen ${date(machine.last_seen)}`}{' '}
              <button disabled={busy || !!machine.revoked_at}
                onClick={() => action(async () => {
                  await api.revokeIngestWorker(machine.id)
                  setMessage(`Access revoked for ${machine.host}. Requests, catalogue and audit are preserved.`)
                })}>
                Revoke {machine.host}
              </button>
            </p>
          ))}
        </section>
      )}
    </main>
  )
}
